import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import RewampScrollRow from "./RewampScrollRow";

// Shared icon-tab strip; labels are pre-resolved strings (the caller owns i18n).
// selectionMode "single" = tablist with panel coupling, "toggle" = deselectable chips.
// appearance "bottomNav" = icon-over-label <nav> grid (tabs + trailing spaced between), aria-current, no panels.

const TabIcon = ({ icon, sx }) => {
  if (typeof icon === "string") {
    return <Box component="img" src={icon} alt="" aria-hidden="true" sx={sx} />;
  }
  return <Box component={icon} aria-hidden="true" sx={sx} />;
};

TabIcon.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]).isRequired,
  sx: PropTypes.object,
};

const BottomNavStrip = ({ tabs, activeId, onChange, ariaLabel, idPrefix, trailing }) => (
  <Box
    component="nav"
    aria-label={ariaLabel}
    sx={{
      display: "grid",
      gridAutoFlow: "column",
      gridAutoColumns: "auto",
      justifyContent: "space-between",
      alignItems: "center",
      px: 2,
      width: "100%",
      boxSizing: "border-box",
    }}
  >
    {tabs.map((tab) => {
      const isActive = tab.id === activeId;
      const activeColor = tab.activeColor ?? "primary.interactive";
      return (
        <Button
          key={tab.id}
          id={`${idPrefix}-${tab.id}`}
          onClick={() => onChange(tab.id)}
          aria-current={isActive ? "page" : undefined}
          sx={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 0.5,
            py: 1,
            px: 0.5,
            borderRadius: 0,
            textTransform: "none",
            color: isActive ? activeColor : "neutral.navInactive",
            backgroundColor: "transparent",
            "&:hover": { backgroundColor: "transparent" },
            "&:focus-visible": {
              outline: (theme) => `3px solid ${theme.palette.accent?.butterscotch}`,
              outlineOffset: "2px",
            },
          }}
        >
          <TabIcon icon={tab.icon} sx={{ width: 24, height: 24, flexShrink: 0 }} />
          <Typography
            variant="body1"
            sx={(theme) => ({
              fontSize: theme.typography.pxToRem(14),
              lineHeight: "1.25rem",
              textAlign: "center",
              fontWeight: isActive
                ? theme.typography.fontWeightBold
                : theme.typography.fontWeightRegular,
            })}
          >
            {tab.label}
          </Typography>
        </Button>
      );
    })}
    {trailing}
  </Box>
);

BottomNavStrip.propTypes = {
  tabs: PropTypes.array.isRequired,
  activeId: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  idPrefix: PropTypes.string.isRequired,
  trailing: PropTypes.node,
};

const RewampTabStrip = ({
  tabs,
  activeId,
  onChange,
  ariaLabel,
  panelId,
  idPrefix,
  selectionMode,
  appearance,
  trailing,
}) => {
  if (appearance === "bottomNav") {
    return (
      <BottomNavStrip
        tabs={tabs}
        activeId={activeId}
        onChange={onChange}
        ariaLabel={ariaLabel}
        idPrefix={idPrefix}
        trailing={trailing}
      />
    );
  }
  return (
    <RewampScrollRow
      rowProps={{
        role: selectionMode === "toggle" ? "group" : "tablist",
        "aria-label": ariaLabel,
      }}
      activeDescendantId={activeId ? `${idPrefix}-${activeId}` : undefined}
      rowSx={{ gap: { xs: 2, md: 4 }, alignItems: "flex-start" }}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;
        const activeColor = tab.activeColor ?? "primary.interactive";
        return (
          // Underline sits on the radius-less wrapper so it stays flat (the Button's radius would curve it).
          <Box
            key={tab.id}
            sx={{
              display: "flex",
              flexShrink: 0,
              pb: "4px",
              borderBottom: "2px solid",
              borderBottomColor: isActive ? activeColor : "transparent",
            }}
          >
            <Button
              id={`${idPrefix}-${tab.id}`}
              onClick={() => onChange(tab.id)}
              {...(selectionMode === "toggle"
                ? { "aria-pressed": isActive }
                : { role: "tab", "aria-selected": isActive, "aria-controls": panelId })}
              sx={{
                minWidth: "fit-content",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                textTransform: "none",
                color: isActive ? activeColor : "neutral.body",
                backgroundColor: "transparent",
                "&:hover": { backgroundColor: "transparent" },
                p: 0,
                "&.MuiButton-sizeMedium": { p: 0 },
              }}
            >
              <Box
                sx={{
                  backgroundColor: tab.backgroundColor,
                  borderRadius: "50%",
                  padding: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={tab.icon}
                  alt=""
                  aria-hidden="true"
                  sx={{ width: 28, height: 28, flexShrink: 0, objectFit: "contain" }}
                />
              </Box>
              <Typography
                variant="body1"
                sx={(theme) => ({
                  fontSize: 16,
                  lineHeight: "24px",
                  fontWeight: isActive
                    ? theme.typography.fontWeightBold
                    : theme.typography.fontWeight,
                })}
              >
                {tab.label}
              </Typography>
            </Button>
          </Box>
        );
      })}
    </RewampScrollRow>
  );
};

RewampTabStrip.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]).isRequired,
      backgroundColor: PropTypes.string,
      activeColor: PropTypes.string,
    })
  ).isRequired,
  activeId: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  panelId: PropTypes.string,
  idPrefix: PropTypes.string,
  selectionMode: PropTypes.oneOf(["single", "toggle"]),
  appearance: PropTypes.oneOf(["default", "bottomNav"]),
  trailing: PropTypes.node,
};

RewampTabStrip.defaultProps = {
  idPrefix: "tab",
  selectionMode: "single",
  appearance: "default",
};

export default RewampTabStrip;
